import { fromPromise } from 'xstate';
import { getStorageJson, safeSessionStorage } from '@repo/dom/storage';
import { queryClient } from '../queryClient';
import { getCartQueryFn, type GetCartResult } from '../graphql/query/getCart';
import { QUERIES, STORAGE_KEYS } from '../utils/constants';
import { queryOrder } from '../utils/queries';
import {
  injectRetailerTheme,
  headerColorsSchema,
  stylesSchema,
} from '../utils/theme';
import type { Workflow, WorkflowData } from './schemaTypes';
import { getConfig } from './config';

export const fetchServerState = fromPromise(
  async ({
    input,
  }: {
    input: {
      config: Workflow | null;
      orderNumber: string | null;
      requestId: string | null;
      token: string | null;
    };
  }) => {
    const { config, orderNumber, requestId, token } = input;
    if (!orderNumber || !requestId || !token) return;

    const { cart } = await queryClient.fetchQuery<GetCartResult>({
      queryKey: [QUERIES.GET_CART, { orderNumber, token, requestId }],
      queryFn: () => getCartQueryFn(requestId),
    });

    if (!cart || cart.items.length === 0)
      return {
        itemSelection: [],
      };

    const isReasonTaskItemScoped = config?.steps.some((step) =>
      step.tasks.some((task) => task.scope === 'item' && task.id === 'reason'),
    );

    const isResolutionTaskItemScoped = config?.steps.some((step) =>
      step.tasks.some(
        (task) => task.scope === 'item' && task.id === 'resolution',
      ),
    );

    const hasItemScopedTasks =
      isReasonTaskItemScoped || isResolutionTaskItemScoped;

    return cart.items.reduce<WorkflowData>((acc, item) => {
      const items = (acc.items ??= {});
      items[item.id] = {
        itemSelection: { id: item.id, quantity: item.quantity },
      };

      const reasonValue = item.reason?.id;
      const reasonId =
        typeof reasonValue === 'string' ? reasonValue : undefined;
      const resolutionValue = item.resolution;
      const resolutionId =
        typeof resolutionValue === 'string' ? resolutionValue : undefined;

      if (hasItemScopedTasks) {
        // itemData is guaranteed to exist — assigned on the line above
        const itemData = items[item.id]!;
        if (isReasonTaskItemScoped) {
          itemData.reason = { reasonId };
        }
        if (isResolutionTaskItemScoped) {
          itemData.resolution = { resolutionId };
        }
      } else {
        if (reasonId !== undefined && !acc.reason) {
          acc.reason = { reasonId };
        }
        if (resolutionId !== undefined && !acc.resolution) {
          acc.resolution = { resolutionId };
        }
      }

      return acc;
    }, {});
  },
);

export const fetchConfig = fromPromise(
  async ({
    input,
  }: {
    input: {
      retailer: string | null;
    };
  }) => {
    const styles = getStorageJson(
      safeSessionStorage,
      STORAGE_KEYS.THEME,
      stylesSchema,
    );
    const headerColors = getStorageJson(
      safeSessionStorage,
      STORAGE_KEYS.HEADER_COLORS,
      headerColorsSchema,
    );
    if (styles) {
      injectRetailerTheme({
        styles,
        headerColors: headerColors ?? undefined,
        options: { retailer: input.retailer },
      });
    }

    const config = await getConfig();
    return config;
  },
);

export const fetchOrder = fromPromise(
  async ({
    input: { email, orderNumber, zipCode, requestId },
  }: {
    input: {
      email: string | null;
      orderNumber: string | null;
      zipCode: string | null;
      requestId: string | null;
    };
  }) => {
    if (!orderNumber) {
      return null;
    }

    const data = await queryOrder({
      email,
      orderNumber,
      zipCode,
      requestId,
    });

    if ('error' in data) {
      throw new Error(data.error);
    }

    safeSessionStorage.setItem(
      STORAGE_KEYS.AUTH_TOKEN,
      data.order.authorization.token,
    );
    return data;
  },
);
